import { useMemo, useState } from "react";

import { TreeItem, SimpleTreeView } from "@mui/x-tree-view";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";
import { DoneAll } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { Box, FormControlLabel, Checkbox, FormControl, Popover, InputAdornment, IconButton, Button, Divider, TextField } from "@mui/material";

/**
 * 특정 레벨의 계층 값을 다중 선택하는 트리 드롭다운.
 * 대상 레벨(levelCd) 노드와 그 상위(조상) 레벨 노드를 트리로 함께 보여주고,
 * 상위 노드 체크 시 그 하위의 대상 레벨 값 전체를 일괄 선택/해제한다.
 *
 * @param {String} label TextField label (번역된 문자열)
 * @param {object[]} hrchies 계층 전체 평면 리스트 (hrchyCd, name, hrchyLv, parentHrchyCd 필요)
 * @param {String} levelCd 선택 대상 레벨 cd (hrchyLv 값)
 * @param {object[]} selected 선택된 대상 레벨 계층 객체 배열
 * @param {function} onChange (objs) => void, 대상 레벨 계층 객체 배열 전달
 * @param {object} styles FormControl sx 추가 스타일 { width: "100%" }
 */
const byName = (a, b) =>
  String(a?.name ?? "").localeCompare(String(b?.name ?? ""), undefined, { numeric: true, sensitivity: "base" });

/**
 * 대상 레벨 노드 + 그 조상 노드만으로 트리 구조를 만든다.
 * isLeafLevel=true 면 대상 레벨을 hrchyLv 대신 leafFlg(최하단 실제 품목/거래처)로 판정한다.
 */
export const buildLevelTree = (hrchies, levelCd, isLeafLevel = false) => {
  const list = hrchies ?? [];
  const byCd = new Map(list.map(h => [h.hrchyCd, h]));
  const nodes = levelCd ? list.filter(h => (isLeafLevel ? h.leafFlg === true : h.hrchyLv === levelCd)) : [];
  const levelCdSet = new Set(nodes.map(n => n.hrchyCd));

  const include = new Map();
  for (const node of nodes) {
    include.set(node.hrchyCd, node);
    let parent = node.parentHrchyCd ? byCd.get(node.parentHrchyCd) : null;
    while (parent && !include.has(parent.hrchyCd)) {
      include.set(parent.hrchyCd, parent);
      parent = parent.parentHrchyCd ? byCd.get(parent.parentHrchyCd) : null;
    }
  }

  const children = new Map();
  const rootList = [];
  for (const node of include.values()) {
    if (node.parentHrchyCd && include.has(node.parentHrchyCd)) {
      if (!children.has(node.parentHrchyCd)) children.set(node.parentHrchyCd, []);
      children.get(node.parentHrchyCd).push(node);
    } else {
      rootList.push(node);
    }
  }
  for (const arr of children.values()) arr.sort(byName);
  rootList.sort(byName);

  // 조상 노드별: 하위의 대상 레벨 hrchyCd 목록
  const descMap = new Map();
  const collect = node => {
    if (levelCdSet.has(node.hrchyCd)) return [node.hrchyCd];
    const cds = (children.get(node.hrchyCd) ?? []).flatMap(collect);
    descMap.set(node.hrchyCd, cds);
    return cds;
  };
  rootList.forEach(collect);

  const ancestors = [...include.values()].filter(n => !levelCdSet.has(n.hrchyCd)).map(n => n.hrchyCd);
  return { roots: rootList, childrenMap: children, levelDescMap: descMap, levelNodes: nodes, levelCdSet, ancestorCds: ancestors };
};

/**
 * 선택된 대상 레벨 계층 객체들을 표시용 이름 목록으로 변환.
 * 하위 대상 레벨 값이 전부 선택된 조상은 조상 이름 하나로 롤업하고,
 * 트리에 없는 항목(NPI 의사 항목 등)은 이름 그대로 뒤에 붙인다.
 */
export const rollupHrchyNames = (hrchies, levelCd, selectedObjs, isLeafLevel = false) => {
  const objs = (selectedObjs ?? []).filter(Boolean);
  const { roots, childrenMap, levelDescMap, levelCdSet } = buildLevelTree(hrchies, levelCd, isLeafLevel);
  const selectedCds = new Set(objs.map(o => o.hrchyCd).filter(cd => levelCdSet.has(cd)));
  const labels = [];
  const walk = node => {
    if (levelCdSet.has(node.hrchyCd)) {
      if (selectedCds.has(node.hrchyCd)) labels.push(node.name);
      return;
    }
    const descCds = levelDescMap.get(node.hrchyCd) ?? [];
    if (descCds.length > 0 && descCds.every(cd => selectedCds.has(cd))) {
      labels.push(node.name);
      return;
    }
    (childrenMap.get(node.hrchyCd) ?? []).forEach(walk);
  };
  roots.forEach(walk);
  for (const o of objs) {
    if (!levelCdSet.has(o.hrchyCd)) labels.push(o?.name ?? "-");
  }
  return labels;
};

const HrchyLevelTreeSelect = ({ label, hrchies, levelCd, selected = [], onChange, styles }) => {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  // 대상 레벨 노드 + 조상 노드만으로 트리 구성
  const { roots, childrenMap, levelDescMap, levelNodes, ancestorCds } = useMemo(
    () => buildLevelTree(hrchies, levelCd),
    [hrchies, levelCd]
  );

  const selectedCds = useMemo(() => new Set((selected ?? []).map(h => h.hrchyCd)), [selected]);

  // 표시 텍스트: 하위 대상 레벨 값이 전부 선택된 조상은 조상 이름 하나로 롤업해서 표시
  const displayText = useMemo(
    () => rollupHrchyNames(hrchies, levelCd, selected).join(", "),
    [hrchies, levelCd, selected]
  );

  const emitChange = nextCdSet => {
    const objs = levelNodes.filter(n => nextCdSet.has(n.hrchyCd)).sort(byName);
    onChange?.(objs);
  };

  const handleToggle = node => {
    const next = new Set(selectedCds);
    if (node.hrchyLv === levelCd) {
      if (next.has(node.hrchyCd)) next.delete(node.hrchyCd);
      else next.add(node.hrchyCd);
    } else {
      const descCds = levelDescMap.get(node.hrchyCd) ?? [];
      const allSelected = descCds.length > 0 && descCds.every(cd => next.has(cd));
      if (allSelected) descCds.forEach(cd => next.delete(cd));
      else descCds.forEach(cd => next.add(cd));
    }
    emitChange(next);
  };

  const renderTree = node => {
    const isLevelNode = node.hrchyLv === levelCd;
    const descCds = isLevelNode ? null : levelDescMap.get(node.hrchyCd) ?? [];
    const checked = isLevelNode
      ? selectedCds.has(node.hrchyCd)
      : descCds.length > 0 && descCds.every(cd => selectedCds.has(cd));
    const indeterminate = !isLevelNode && !checked && descCds.some(cd => selectedCds.has(cd));

    return (
      <TreeItem
        itemId={node.hrchyCd}
        key={node.hrchyCd}
        sx={{ boxSizing: "border-box" }}
        label={
          <FormControlLabel
            sx={{ width: "95%" }}
            control={
              <Checkbox
                sx={{ p: 0.5 }}
                checked={checked}
                size="small"
                indeterminate={indeterminate}
                onChange={() => handleToggle(node)}
                onClick={e => e.stopPropagation()}
              />
            }
            label={<Box sx={{ padding: "3px 9px", width: "95%" }}>{node.name}</Box>}
          />
        }>
        {(childrenMap.get(node.hrchyCd) ?? []).map(child => renderTree(child))}
      </TreeItem>
    );
  };

  return (
    <>
      <FormControl size="small" sx={{ minWidth: 230, ...styles }}>
        <TextField
          size="small"
          label={label}
          type="string"
          onClick={e => setAnchorEl(e.currentTarget)}
          value={displayText}
          autoComplete="off"
          InputProps={{
            readOnly: true,
            endAdornment: (
              <InputAdornment position="end">
                <IconButton aria-label="open hierarchy tree" sx={{ padding: 0 }} size="small">
                  {open ? <ArrowDropUpIcon /> : <ArrowDropDownIcon />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      </FormControl>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        disableAutoFocus
        disableEnforceFocus
        TransitionProps={{ timeout: 0 }}>
        <Box>
          <Box sx={{ minWidth: 230, maxHeight: 360, overflowY: "auto", overflowX: "hidden", ...styles }}>
            {roots.length === 0 ? (
              <Box sx={{ padding: "8px 16px", minWidth: 230, color: "#888" }}>{"No Options"}</Box>
            ) : (
              <SimpleTreeView
                key={levelCd}
                defaultExpandedItems={ancestorCds}
                sx={{ paddingLeft: "5px", minWidth: 230 }}>
                {roots.map(node => renderTree(node))}
              </SimpleTreeView>
            )}
          </Box>
          <Divider sx={{ backgroundColor: "primary.main", borderColor: "primary.main", borderWidth: 0.5, opacity: 0.2 }} />
          <Box sx={{ padding: "8px", display: "flex", justifyContent: "flex-end" }}>
            <Button
              sx={{ mr: 1.1 }}
              variant="contained"
              startIcon={<DoneAll />}
              size="small"
              color="primary"
              onClick={() => setAnchorEl(null)}>
              {t("confirm")}
            </Button>
          </Box>
        </Box>
      </Popover>
    </>
  );
};

export default HrchyLevelTreeSelect;
